import React, { useEffect, useState } from "react";
import { MessageSquare, Loader2, Save, Send, PauseCircle, PlayCircle, ChevronDown, ChevronUp, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Card, Btn, Input, Select, Textarea } from "../UI";

const SMS_KINDS = ["start_sms", "report_sms"];
const EMAIL_KINDS = ["start_email", "session_end_email", "eod_email"];

const Toggle = ({ on, onChange, testid, label }) => (
  <button type="button" onClick={() => onChange(!on)} data-testid={testid} aria-pressed={on}
    className={`inline-flex items-center gap-2 text-xs rounded-full px-3 py-1 border transition-colors ${on ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200" : "border-white/10 text-slate-400"}`}>
    <span className={`w-2 h-2 rounded-full ${on ? "bg-emerald-400" : "bg-slate-600"}`} /> {label} · {on ? "on" : "off"}
  </button>
);

const Preview = ({ p }) => !p ? null : (
  <div className="rounded-md border border-white/10 bg-elevated p-3 text-xs text-slate-300 whitespace-pre-wrap" data-testid="followup-preview">
    {p.channel === "email" && <div className="text-amber-300 mb-1">Subject: {p.subject}</div>}
    {p.channel === "email" ? p.text.split("\n\n")[0] : p.body}
  </div>
);

export const FollowupControls = ({ onSaved }) => {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testKind, setTestKind] = useState("start_email");

  const load = () => api.get("/promotions/followups/settings").then(({ data }) => setS(data?.kinds && data?.enabled ? data : null)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;

  const body = () => ({ paused_all: s.paused_all, enabled: s.enabled, eod_hour_et: s.eod_hour_et,
    templates: { sms: s.templates.sms, email: s.templates.email } });
  const setTpl = (chan, kind, field, value) => setS({ ...s, templates: { ...s.templates, [chan]: { ...s.templates[chan], [kind]: chan === "sms" ? value : { ...s.templates.email[kind], [field]: value } } } });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/promotions/followups/settings", body()); setS(data); toast.success("Follow-up settings saved"); onSaved?.(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); }
    finally { setBusy(false); }
  };
  const togglePause = async () => {
    try { const { data } = await api.put("/promotions/followups/settings", { paused_all: !s.paused_all }); setS(data); toast.success(data.paused_all ? "All follow-ups paused" : "Follow-ups resumed"); }
    catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  const doPreview = async (kind) => {
    try { const { data } = await api.post("/promotions/followups/preview", { kind, settings: body() }); setPreview(data); }
    catch (e) { toast.error(errMsg(e, "Preview failed")); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/promotions/followups/test", { kind: testKind, to_phone: testPhone || null, settings: body() });
      toast.success(data.channel === "sms" ? `Test SMS sent to ${data.to}` : `Test email sent to ${data.to}`);
    } catch (e) { toast.error(errMsg(e, "Test send failed")); }
    finally { setBusy(false); }
  };
  const resetKind = (chan, kind) => setS({ ...s, templates: { ...s.templates, [chan]: { ...s.templates[chan], [kind]: s.defaults[chan][kind] } } });
  const label = (k) => s.kinds.find((x) => x.key === k)?.label || k;

  return (
    <Card className="!p-5 space-y-4" data-testid="followup-controls">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => setOpen(!open)} className="font-display font-bold text-sm inline-flex items-center gap-2" data-testid="followup-controls-toggle">
          <MessageSquare className="w-4 h-4 text-amber-300" /> Follow-up messages {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] ${s.paused_all ? "text-rose-300" : "text-slate-500"}`} data-testid="followup-pause-state">{s.paused_all ? "All follow-ups paused" : `${Object.values(s.enabled).filter(Boolean).length}/${s.kinds.length} messages on · recap ${s.eod_hour_et}:00 ET`}</span>
          <Btn variant="secondary" onClick={togglePause} data-testid="followup-pause-all-btn">{s.paused_all ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />} {s.paused_all ? "Resume all" : "Pause all"}</Btn>
        </div>
      </div>
      {open && (
        <div className="space-y-5" data-testid="followup-editor">
          <div className="flex flex-wrap gap-2" data-testid="followup-kind-toggles">
            {s.kinds.map((k) => <Toggle key={k.key} on={!!s.enabled[k.key]} label={k.label} testid={`followup-toggle-${k.key}`} onChange={(v) => setS({ ...s, enabled: { ...s.enabled, [k.key]: v } })} />)}
          </div>
          <div className="text-[11px] text-slate-500">Placeholders: {s.placeholders.map((p) => <code key={p} className="mx-0.5 px-1 rounded bg-white/5 text-amber-200">{`{${p}}`}</code>)}</div>
          <div className="grid lg:grid-cols-2 gap-4">
            {SMS_KINDS.map((k) => (
              <div key={k} className="space-y-1" data-testid={`followup-tpl-${k}`}>
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400 font-medium">{label(k)}</span>
                  <div className="flex gap-2"><button onClick={() => doPreview(k)} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid={`followup-preview-${k}`}><Eye className="w-3 h-3" /> Preview</button>
                    <button onClick={() => resetKind("sms", k)} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid={`followup-reset-${k}`}><RotateCcw className="w-3 h-3" /> Default</button></div></div>
                <Textarea value={s.templates.sms[k]} onChange={(e) => setTpl("sms", k, null, e.target.value)} data-testid={`followup-sms-${k}`} />
                <div className="text-[10px] text-slate-500">{s.templates.sms[k].length}/320 · include {"{reopen_url}"}</div>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            {EMAIL_KINDS.map((k) => (
              <div key={k} className="space-y-2" data-testid={`followup-tpl-${k}`}>
                <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-400 font-medium">{label(k)}</span>
                  <div className="flex gap-2"><button onClick={() => doPreview(k)} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid={`followup-preview-${k}`}><Eye className="w-3 h-3" /> Preview</button>
                    <button onClick={() => resetKind("email", k)} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid={`followup-reset-${k}`}><RotateCcw className="w-3 h-3" /> Default</button></div></div>
                <Input value={s.templates.email[k].subject} onChange={(e) => setTpl("email", k, "subject", e.target.value)} placeholder="Subject" data-testid={`followup-subject-${k}`} />
                <Textarea value={s.templates.email[k].intro} onChange={(e) => setTpl("email", k, "intro", e.target.value)} placeholder="Intro paragraph" data-testid={`followup-intro-${k}`} />
              </div>
            ))}
          </div>
          <Preview p={preview} />
          <div className="flex flex-wrap items-end gap-3 border-t border-white/5 pt-4">
            <Select label="End-of-day recap hour (ET)" value={s.eod_hour_et} onChange={(e) => setS({ ...s, eod_hour_et: parseInt(e.target.value) })} className="w-48" data-testid="followup-eod-hour">
              {Array.from({ length: 17 }, (_, i) => i + 6).map((h) => <option key={h} value={h}>{h > 12 ? `${h - 12}:00 pm` : h === 12 ? "12:00 pm" : `${h}:00 am`}</option>)}
            </Select>
            <Btn onClick={save} disabled={busy} data-testid="followup-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes</Btn>
            <div className="ml-auto flex flex-wrap items-end gap-2">
              <Select label="Send me a test" value={testKind} onChange={(e) => setTestKind(e.target.value)} className="w-56" data-testid="followup-test-kind">
                {s.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </Select>
              {testKind.endsWith("_sms") && <Input label="To phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+1 469 555 0100" className="w-44" data-testid="followup-test-phone" />}
              <Btn variant="secondary" onClick={sendTest} disabled={busy} data-testid="followup-test-btn"><Send className="w-4 h-4" /> Send test</Btn>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
